/**
 * useVersionCheck — drives the force-update gate.
 *
 * Polls /api/version (on mount, on tab focus / visibility, and on an interval)
 * and derives ONE of two signals depending on platform:
 *
 *  - **Native** (iOS/Android): the JS bundle is baked into the binary, so the
 *    server `buildId` is irrelevant. Instead we compare the installed binary
 *    version (`App.getInfo().version`) against the server-published
 *    `minAppVersion`. Below the minimum → `nativeUpdateRequired`, and the gate
 *    sends the user to the store (the only thing Apple allows; on Android the
 *    Play in-app immediate update is wired separately once the plugin lands).
 *
 *  - **Web** (desktop/SPA): no app version exists, but the loaded bundle bakes
 *    in its `buildId`. When the deployed `buildId` differs, a newer build is
 *    live → `webReloadPending`, and the gate hard-reloads (per product choice,
 *    web always auto-reloads after a short grace).
 *
 * Fail-open throughout: any fetch/parse error is swallowed and retried next
 * tick. A config glitch must never gate a healthy user.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { App as CapacitorApp } from '@capacitor/app'
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update'
import { apiFetch } from '@/lib/api'
import { isNative, isAndroid } from '@/lib/platform'
import { isUpdateRequired, isBuildStale, type VersionPayload } from '@/lib/version-check'
import { STORE_URLS, APPLE_APP_STORE_ID } from '@/lib/store-links'
import buildInfo from '@/build-info.json'

// Background re-check cadence. Focus / visibility changes also trigger a check,
// so this is just the floor for a long-lived foreground tab.
const POLL_INTERVAL_MS = 15 * 60_000
const FETCH_TIMEOUT_MS = 5000

export interface VersionCheckState {
  /** Native binary is below the published minimum — show the blocking gate. */
  nativeUpdateRequired: boolean
  /** A newer web build is deployed — the gate should hard-reload. */
  webReloadPending: boolean
  /** Open the platform store (native gate's update button). */
  openStore: () => void
}

export function useVersionCheck(): VersionCheckState {
  const [nativeUpdateRequired, setNativeUpdateRequired] = useState(false)
  const [webReloadPending, setWebReloadPending] = useState(false)
  // Installed native version is immutable for the process lifetime — cache it.
  const installedRef = useRef<string | null>(null)

  const check = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/version?_=${Date.now()}`, {
        cache: 'no-store',
        signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
      })
      if (!res.ok) return
      const data = (await res.json()) as VersionPayload

      if (isNative()) {
        if (installedRef.current == null) {
          const info = await CapacitorApp.getInfo()
          installedRef.current = info.version
        }
        if (isUpdateRequired(installedRef.current, data.minAppVersion)) {
          setNativeUpdateRequired(true)
        }
      } else if (isBuildStale((buildInfo as VersionPayload).buildId, data.buildId)) {
        setWebReloadPending(true)
      }
    } catch {
      /* offline / transient — ignore, retry next tick */
    }
  }, [])

  useEffect(() => {
    void check()
    const interval = setInterval(() => void check(), POLL_INTERVAL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check()
    }
    const onFocus = () => void check()
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onFocus)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onFocus)
    }
  }, [check])

  const openStore = useCallback(async () => {
    try {
      if (isAndroid()) {
        // Prefer Play's full-screen, in-place immediate update. Requires a real
        // Play install whose store version is actually higher (so it's a no-go
        // on sideloaded/dev builds, and when min_app_version is set above the
        // published version) — fall back to the store listing in those cases.
        const info = await AppUpdate.getAppUpdateInfo()
        if (
          info.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE &&
          info.immediateUpdateAllowed
        ) {
          await AppUpdate.performImmediateUpdate()
          return
        }
        await AppUpdate.openAppStore()
        return
      }
      // iOS: Apple has no in-place update — open the App Store listing natively.
      // openAppStore() works under limitsNavigationsToAppBoundDomains, where a
      // window.open to apps.apple.com may be blocked.
      await AppUpdate.openAppStore({ appId: APPLE_APP_STORE_ID })
    } catch {
      // Plugin missing (web, or a native build without it) → best-effort link.
      window.open(isAndroid() ? STORE_URLS.android : STORE_URLS.ios, '_blank')
    }
  }, [])

  return { nativeUpdateRequired, webReloadPending, openStore }
}
